import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useAccountLockout } from '@/contexts/AccountLockoutContext';
import AccountLockedPage from '@/pages/AccountLocked';

interface AccountLockoutGuardProps {
  children: React.ReactNode;
  authState?: string;
}

/**
 * Guard component that checks account lockout status after Cognito authentication.
 * If account is locked, displays the AccountLockedPage instead of the app.
 *
 * Checks lockout status on:
 * - Initial authentication
 * - Route changes
 * - Window focus (after being idle for >5 minutes)
 *
 * This activity-based approach is more cost-effective than polling.
 */
export function AccountLockoutGuard({ children, authState }: AccountLockoutGuardProps) {
  const { lockoutStatus, isLoading, checkLockoutStatus } = useAccountLockout();
  const location = useLocation();
  const lastFocusCheckRef = useRef<number>(Date.now());
  const isAuthenticatedRef = useRef(false);

  // Initial check when authenticated
  useEffect(() => {
    if (authState === 'authenticated' && !isAuthenticatedRef.current) {
      isAuthenticatedRef.current = true;
      checkLockoutStatus();
    } else if (authState !== 'authenticated') {
      isAuthenticatedRef.current = false;
    }
  }, [authState, checkLockoutStatus]);

  // Check on route changes
  useEffect(() => {
    if (authState === 'authenticated') {
      checkLockoutStatus();
    }
  }, [location.pathname, authState, checkLockoutStatus]);

  // Check on window focus (if idle for >5 minutes)
  useEffect(() => {
    if (authState !== 'authenticated') {
      return;
    }

    const handleFocus = () => {
      const now = Date.now();
      const timeSinceLastCheck = now - lastFocusCheckRef.current;
      const FIVE_MINUTES = 5 * 60 * 1000;

      // Only check if it's been >5 minutes since last check
      if (timeSinceLastCheck > FIVE_MINUTES) {
        lastFocusCheckRef.current = now;
        checkLockoutStatus();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [authState, checkLockoutStatus]);

  // Still loading → show nothing (spinner handled by InitialLoader)
  if (isLoading) return null;

  // Account is locked → show lockout page
  if (lockoutStatus?.is_locked) {
    return <AccountLockedPage lockoutInfo={lockoutStatus} />;
  }

  // Not locked → render normal app
  return <>{children}</>;
}
